import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatFullDate, formatNumber, formatRelativeTime } from "@/lib/format";
import type { ApiCallLog, ApiClient, ApiKey } from "@/pages/admin/apikeys/hooks/useApiKeys";

interface ApiClientPanelProps {
  clients: ApiClient[];
  keys: ApiKey[];
  logs: ApiCallLog[];
  loading: boolean;
  onCreate: () => void;
  onEdit: (client: ApiClient) => void;
  onToggleStatus: (client: ApiClient) => void;
  onDelete: (client: ApiClient) => void;
  onAddKey: (client: ApiClient) => void;
  onToggleKey: (key: ApiKey) => void;
  onDeleteKey: (key: ApiKey) => void;
}

// 对接应用列表：每个应用卡片下可展开查看其全部密钥，支持签发 / 吊销 / 删除与状态切换。
export default function ApiClientPanel({
  clients,
  keys,
  logs,
  loading,
  onCreate,
  onEdit,
  onToggleStatus,
  onDelete,
  onAddKey,
  onToggleKey,
  onDeleteKey,
}: ApiClientPanelProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<number | null>(null);

  const keysByClient = useMemo(() => {
    const map = new Map<number, ApiKey[]>();
    keys.forEach((key) => {
      const list = map.get(key.client_id) ?? [];
      list.push(key);
      map.set(key.client_id, list);
    });
    return map;
  }, [keys]);

  const lastUsedByClient = useMemo(() => {
    const map = new Map<number, string>();
    logs.forEach((log) => {
      if (log.client_id == null) return;
      const prev = map.get(log.client_id);
      if (!prev || new Date(log.created_at).getTime() > new Date(prev).getTime()) {
        map.set(log.client_id, log.created_at);
      }
    });
    return map;
  }, [logs]);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-apps-2-line" />
          </span>
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("apikeys.clientsTitle")}</h2>
            <p className="text-[11px] text-foreground-500">{t("apikeys.clientsSub")}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onCreate}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-add-line" />
          </span>
          {t("apikeys.createClient")}
        </button>
      </div>

      {loading ? (
        <div className="mt-4 flex flex-col gap-3">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-20 rounded-md bg-background-100" />
          ))}
        </div>
      ) : clients.length === 0 ? (
        <div className="mt-4 flex flex-col items-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100 py-10 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-plug-2-line text-lg" />
          </span>
          <p className="text-sm font-medium text-foreground-800">{t("apikeys.noClient")}</p>
          <p className="text-xs text-foreground-500">{t("apikeys.noClientDesc")}</p>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          {clients.map((client) => {
            const clientKeys = keysByClient.get(client.id) ?? [];
            const activeKeys = clientKeys.filter((key) => key.status === "active").length;
            const lastUsed = lastUsedByClient.get(client.id);
            const isOpen = expanded === client.id;
            const suspended = client.status !== "active";
            return (
              <div key={client.id} className="rounded-md border border-background-200 bg-background-50">
                <div className="flex flex-wrap items-center justify-between gap-3 p-3.5">
                  <div className="flex min-w-0 items-start gap-3">
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                        suspended ? "bg-background-200 text-foreground-500" : "bg-primary-100 text-primary-700"
                      }`}
                    >
                      <i className="ri-plug-2-line text-base" />
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-medium text-foreground-950">{client.name}</p>
                        <span
                          className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            suspended ? "bg-accent-100 text-accent-800" : "bg-primary-100 text-primary-700"
                          }`}
                        >
                          <span className="flex h-3 w-3 items-center justify-center">
                            <i className={suspended ? "ri-pause-circle-line" : "ri-checkbox-circle-line"} />
                          </span>
                          {suspended ? t("apikeys.suspended") : t("apikeys.activeStatus")}
                        </span>
                        <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-900">
                          <span className="flex h-3 w-3 items-center justify-center">
                            <i className="ri-key-2-line" />
                          </span>
                          {t("apikeys.clientKeys", { count: clientKeys.length })} · {activeKeys}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-[11px] text-foreground-500">
                        {client.owner_name || t("apikeys.unknown")}
                        {client.contact_email ? ` · ${client.contact_email}` : ""}
                      </p>
                      <p className="mt-0.5 text-[11px] text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line" />
                          {lastUsed
                            ? t("apikeys.clientLastUsed", { time: formatRelativeTime(lastUsed) })
                            : t("apikeys.clientNeverUsed")}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onAddKey(client)}
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-2.5 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-add-line" />
                      </span>
                      {t("apikeys.addKey")}
                    </button>
                    <button
                      type="button"
                      onClick={() => onEdit(client)}
                      className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-edit-line" />
                      </span>
                      {t("apikeys.edit")}
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleStatus(client)}
                      className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className={suspended ? "ri-play-circle-line" : "ri-pause-circle-line"} />
                      </span>
                      {suspended ? t("apikeys.resume") : t("apikeys.suspend")}
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(client)}
                      className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-2.5 py-1.5 text-xs font-medium text-accent-800 transition-colors hover:bg-accent-100"
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-delete-bin-line" />
                      </span>
                      {t("apikeys.delete")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : client.id)}
                      aria-expanded={isOpen}
                      className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100"
                      title={t("apikeys.clientKeys", { count: clientKeys.length })}
                    >
                      <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-background-200 p-3.5">
                    {clientKeys.length === 0 ? (
                      <p className="rounded-md border border-dashed border-background-300 bg-background-100 px-3 py-4 text-center text-xs text-foreground-500">
                        {t("apikeys.noKey")}
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[720px] text-sm">
                          <thead>
                            <tr className="bg-background-100 text-left text-[11px] font-medium text-foreground-600">
                              <th className="px-3 py-2">{t("apikeys.keyName")}</th>
                              <th className="px-3 py-2">{t("apikeys.keyPrefix")}</th>
                              <th className="px-3 py-2">{t("apikeys.keyRate")}</th>
                              <th className="px-3 py-2">{t("apikeys.keyQuota")}</th>
                              <th className="px-3 py-2">{t("apikeys.keyStatus")}</th>
                              <th className="px-3 py-2">{t("apikeys.keyLastUsed")}</th>
                              <th className="px-3 py-2 text-right">{t("apikeys.edit")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clientKeys.map((key) => {
                              const revoked = key.status !== "active";
                              return (
                                <tr key={key.id} className="border-t border-background-200">
                                  <td className="px-3 py-2.5">
                                    <p className="text-xs font-medium text-foreground-900">{key.name}</p>
                                    {key.expires_at && (
                                      <p className="text-[11px] text-foreground-400">
                                        {t("apikeys.keyExpires")}：{formatFullDate(key.expires_at)}
                                      </p>
                                    )}
                                  </td>
                                  <td className="px-3 py-2.5 font-mono text-[11px] text-foreground-700">
                                    {key.key_prefix}…
                                  </td>
                                  <td className="px-3 py-2.5 text-[11px] text-foreground-700">
                                    {t("apikeys.rateUnit", { count: formatNumber(key.rate_limit_per_min) })}
                                  </td>
                                  <td className="px-3 py-2.5 text-[11px] text-foreground-700">
                                    {key.quota_per_day > 0
                                      ? t("apikeys.quotaUnit", { count: formatNumber(key.quota_per_day) })
                                      : t("apikeys.keyUnlimited")}
                                  </td>
                                  <td className="px-3 py-2.5">
                                    <span
                                      className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                        revoked ? "bg-accent-100 text-accent-800" : "bg-primary-100 text-primary-700"
                                      }`}
                                    >
                                      {revoked ? t("apikeys.keyRevokedStatus") : t("apikeys.keyActiveStatus")}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2.5 text-[11px] text-foreground-500">
                                    {key.last_used_at ? formatRelativeTime(key.last_used_at) : t("apikeys.keyNever")}
                                  </td>
                                  <td className="px-3 py-2.5">
                                    <div className="flex items-center justify-end gap-2">
                                      <button
                                        type="button"
                                        onClick={() => onToggleKey(key)}
                                        className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2 py-1 text-[11px] font-medium text-foreground-800 transition-colors hover:bg-background-100"
                                      >
                                        {revoked ? t("apikeys.enable") : t("apikeys.revoke")}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => onDeleteKey(key)}
                                        className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-accent-700 transition-colors hover:bg-accent-50"
                                        title={t("apikeys.delete")}
                                      >
                                        <i className="ri-delete-bin-line text-xs" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}